import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import type { SourceSpan } from '@eluve/fhir-review-lifecycle';

const c = initContract();

export const itemStateSchema = z.enum(['added', 'changed', 'unchanged']);
export const decisionSchema = z.enum([
  'pending',
  'approved',
  'edited',
  'rejected',
]);
/** Wire encoding of the shared `SourceSpan`. The `satisfies` keeps the two in
 *  lockstep — drift fails to compile rather than at the client boundary. */
export const sourceSpanSchema = z.object({
  start: z.number().int(),
  end: z.number().int(),
  text: z.string(),
}) satisfies z.ZodType<SourceSpan>;

export const carePlanEvidenceSchema = z.object({
  quote: z.string(),
  sourceType: z.enum(['transcript', 'note', 'unknown']),
  sourceAppointmentId: z.string(),
  span: z
    .object({
      start: z.number().int(),
      end: z.number().int(),
    })
    .nullable(),
});

export const pendingChangeSchema = z.object({
  proposalId: z.string(),
  /** `cadence` is the schedule card's row — the only field whose proposal
   *  targets the CarePlan itself rather than one of its items (ENG-5748). */
  field: z.enum([
    'targetDate',
    'text',
    'status',
    'value',
    'isPrimary',
    'cadence',
  ]),
  previous: z.string().nullable(),
  proposed: z.string().nullable(),
  confidence: z.number().nullable(),
  sourceSpan: sourceSpanSchema.nullable(),
  /** 'approved' = confirmed this visit and still undoable (applied-proposal
   *  memory); additive for older clients that only knew pending/rejected. */
  decision: z.enum(['pending', 'rejected', 'approved']).default('pending'),
});

export const measureCodeSchema = z.object({
  system: z.string(),
  code: z.string(),
});

export const measureCatalogEntrySchema = z.object({
  key: z.string(),
  label: z.string(),
  system: z.string(),
  code: z.string(),
  display: z.string(),
  unit: z.string().optional(),
  kind: z.enum(['quantity', 'string']),
});

/** An AI-proposed change to a measure's plan target — carried on the owning
 *  Goal as a `measureTarget` pending change, surfaced on the measure row.
 *  `previous`/`proposed` are display-ready (e.g. ">= 80 °"). */
export const measureTargetChangeSchema = z.object({
  proposalId: z.string(),
  goalId: z.string(),
  previous: z.string().nullable(),
  proposed: z.string(),
  decision: z.enum(['pending', 'rejected', 'approved']).default('pending'),
});

export const measureTargetSchema = z.object({
  measureCode: measureCodeSchema,
  // Additive: the catalog key of the measure this target describes, so a goal
  // row can be joined to its measure row without re-resolving the coding.
  // Null when the coding is not in the catalog.
  measureKey: z.string().nullable().default(null),
  // Additive: the body site the target was picked at, so bilateral readings of
  // one measure do not collide. Null when the target records no site (an older
  // api-nest, or a pick from a site-less reading), and a null site matches any
  // row by key alone, which is the pre-ENG-5790 tiebreak.
  bodySite: z.string().nullable().default(null),
  label: z.string(),
  comparator: z.enum(['>=', '<=', '>', '<']).nullable(),
  value: z.number().nullable(),
  low: z.number().nullable(),
  high: z.number().nullable(),
  unit: z.string().nullable(),
});

export const goalVmSchema = z.object({
  id: z.string(),
  text: z.string(),
  targetDate: z.string().nullable(),
  done: z.boolean(),
  isPrimary: z.boolean(),
  findingBacked: z.boolean(),
  itemState: itemStateSchema,
  pendingChanges: z.array(pendingChangeSchema).default([]),
  pendingChange: pendingChangeSchema.nullable(),
  decision: decisionSchema,
  measureTargets: z.array(measureTargetSchema),
  sourceSpan: sourceSpanSchema.nullable(),
  confidence: z.number().nullable(),
  evidence: z.string().nullable(),
  sourceEvidence: carePlanEvidenceSchema.nullable(),
  // Additive: plan membership. Older payloads omit it and parse to true, the
  // pre-link behavior where every row on the surface was on the plan.
  inPlan: z.boolean().default(true),
  // Additive: whether the row is in the review lifecycle at all. Older
  // payloads omit it and parse to false, matching the pre-link behavior where
  // a `pending` decision was the only signal a row awaited review.
  reviewTracked: z.boolean().default(false),
  // Additive: authored by a human rather than an agent. Older payloads omit it
  // and parse to false, the pre-derivation behavior where every row read as AI
  // content.
  manuallyAdded: z.boolean().default(false),
});

export const trendPointSchema = z.object({
  value: z.number(),
  unit: z.string().nullable(),
  date: z.string(),
  encounterId: z.string().nullable(),
});

/**
 * The reading a measure carries in from an earlier visit, when this visit has
 * not measured it again. Separate from `thisVisit` on purpose: an established
 * measure's row stays blank and editable so the clinician can enter today's
 * value, and this is the context beside it — never the row's own value
 * (ENG-5761).
 */
export const carriedMeasureSchema = z.object({
  value: z.number().nullable(),
  /** A qualitative reading ("50/50", "symmetrical") carries forward the same
   *  way a number does — see `isObjectiveMeasureObservation`. */
  stringValue: z.string().nullable(),
  unit: z.string().nullable(),
  /** Date of the visit that captured it, `YYYY-MM-DD`, already local to that
   *  appointment. Null when the capturing appointment could not be resolved. */
  capturedOn: z.string().nullable(),
});

export const measureVmSchema = z.object({
  key: z.string(),
  label: z.string(),
  bodySite: z.string().nullable().default(null),
  target: measureTargetSchema.nullable(),
  /**
   * Which way the measure has to move to count as improvement. Comes from the
   * catalog's `betterDirection`, not from the target's comparator — a measure
   * with no target still improves in one particular direction, and inferring it
   * from an absent comparator read a rising pain score as progress (ENG-5761).
   * Additive: older payloads omit it and parse to the commoner direction.
   */
  betterDirection: z.enum(['higher', 'lower']).default('higher'),
  // Additive: older payloads omit it and parse to null.
  carried: carriedMeasureSchema.nullable().default(null),
  thisVisit: z
    .object({
      observationId: z.string(),
      value: z.number().nullable(),
      stringValue: z.string().nullable(),
      unit: z.string().nullable(),
      decision: decisionSchema,
      itemState: itemStateSchema,
      pendingChanges: z.array(pendingChangeSchema).default([]),
      pendingChange: pendingChangeSchema.nullable(),
      confidence: z.number().nullable(),
      evidence: z.string().nullable(),
      sourceEvidence: carePlanEvidenceSchema.nullable(),
    })
    .nullable(),
  trend: z.object({
    points: z.array(trendPointSchema).max(5),
    /**
     * Readings still awaiting review, oldest first, one per encounter. Kept
     * beside `points` rather than merged into it so an older bundle renders
     * exactly the series it renders today. The displayed visit's own pending
     * reading appears in BOTH arrays: `points` has carried it since before
     * drafts were representable, and removing it there would blank the last
     * point for old bundles mid-rollout. Readers draw the union keyed by
     * `encounterId` and treat an encounter named here as a draft.
     * Additive: older payloads omit it and parse to no drafts at all.
     */
    draftPoints: z.array(trendPointSchema).max(5).default([]),
    prev: z.number().nullable(),
    current: z.number().nullable(),
    delta: z.number().nullable(),
    /**
     * Whether the displayed visit contributed the last point. False means the
     * series ends at an earlier visit, which the sparkline draws as a dashed
     * trailing segment so a gap reads as a gap. Additive: older payloads omit
     * it and parse to false.
     */
    capturedThisVisit: z.boolean().default(false),
  }),
  // Additive: older payloads omit it and parse to null.
  targetChange: measureTargetChangeSchema.nullable().default(null),
  // Additive: authored by a human rather than an agent, read off this visit's
  // reading. Older payloads omit it and parse to false, the pre-derivation
  // behavior where every row read as AI content.
  manuallyAdded: z.boolean().default(false),
});

export const actionItemVmSchema = z.object({
  id: z.string(),
  text: z.string(),
  itemState: itemStateSchema,
  pendingChanges: z.array(pendingChangeSchema).default([]),
  pendingChange: pendingChangeSchema.nullable(),
  decision: decisionSchema,
  sourceSpan: sourceSpanSchema.nullable(),
  confidence: z.number().nullable(),
  evidence: z.string().nullable(),
  sourceEvidence: carePlanEvidenceSchema.nullable(),
  // Additive: plan membership. Older payloads omit it and parse to true, the
  // pre-link behavior where every row on the surface was on the plan.
  inPlan: z.boolean().default(true),
  // Additive: whether the row is in the review lifecycle at all. Older
  // payloads omit it and parse to false, matching the pre-link behavior where
  // a `pending` decision was the only signal a row awaited review.
  reviewTracked: z.boolean().default(false),
  // Additive: authored by a human rather than an agent. Older payloads omit it
  // and parse to false, the pre-derivation behavior where every row read as AI
  // content.
  manuallyAdded: z.boolean().default(false),
});

export const scheduleSegmentSchema = z.object({
  frequency: z.number().int().min(1),
  period: z.number().int().min(1).nullable(),
  periodUnit: z.enum(['d', 'wk', 'mo']),
  duration: z.number().int().min(1).nullable(),
  durationUnit: z.enum(['d', 'wk', 'mo']).nullable(),
});

export const ladderStepSchema = z.object({
  number: z.number().int(),
  date: z.string().nullable(),
  status: z.enum(['completed', 'current', 'upcoming']),
  booked: z.boolean(),
  pinned: z.boolean(),
  reassess: z.boolean(),
  encounterId: z.string().nullable(),
  appointmentId: z.string().nullable(),
});

export const reassessSchema = z.object({
  afterVisits: z.number().int().nullable(),
  afterDays: z.number().int().nullable(),
  bookedManually: z.boolean(),
});

export const checklistItemSchema = z.object({
  key: z.enum([
    'sessionFrequency',
    'phase',
    'objectiveMeasures',
    'actionItems',
    'goal',
    'reassessBooked',
    'findingsReviewed',
  ]),
  label: z.string(),
  done: z.boolean(),
  manual: z.boolean(),
});

/**
 * A condition shown on the visit's Conditions section — the plan's addressed
 * conditions plus this visit's extracted drafts (ENG-5579). Everything past
 * `text` is additive: an older client reading only `{ id, text }` is unaffected,
 * and the defaults keep an older API's payload parseable server-side.
 *
 * The three date fields ship raw and unresolved. Which one a row displays is
 * decided client-side by the shared `conditionDate` (`@eluve/fhir-utils`), the
 * same resolver the patient profile uses against live FHIR — so the two surfaces
 * cannot label the same condition differently.
 */
export const treatingConditionSchema = z.object({
  id: z.string(),
  text: z.string(),
  clinicalStatus: z.string().nullable().default(null),
  icd10Code: z.string().nullable().default(null),
  /** Laterality / site ("right shoulder") — often the field that reveals an
   *  extraction picked the wrong side, so it belongs on a review surface. */
  bodySite: z.string().nullable().default(null),
  onsetDate: z.string().nullable().default(null),
  onsetText: z.string().nullable().default(null),
  recordedDate: z.string().nullable().default(null),
  /** A captured denial ("denies chest pain"), drafted as `refuted`. */
  isDenial: z.boolean().default(false),
  /**
   * Review posture, matching the plan-row sections so the visit-level
   * Conditions section reuses `ItemReviewRow` and `itemReviewPosture`
   * unchanged. `unchanged` means already merged into the plan — inert, no
   * verdict controls; this visit's extracted conditions are `added`.
   * Defaulted to the inert pair so an older API's payload renders read-only
   * rather than offering buttons for state the server can't describe.
   */
  itemState: itemStateSchema.default('unchanged'),
  decision: decisionSchema.default('pending'),
  /** Quote + transcript offsets for the evidence drawer, when locatable. */
  sourceEvidence: carePlanEvidenceSchema.nullable().default(null),
  // Additive: plan membership. Older payloads omit it and parse to true, the
  // pre-link behavior where every row on the surface was on the plan.
  inPlan: z.boolean().default(true),
  // Additive: whether the row is in the review lifecycle at all. Older
  // payloads omit it and parse to false, matching the pre-link behavior where
  // a `pending` decision was the only signal a row awaited review.
  reviewTracked: z.boolean().default(false),
});

export const carePlanViewModelSchema = z.object({
  plan: z
    .object({
      id: z.string(),
      status: z.string(),
      posture: z.enum(['proposal', 'accepted']),
      title: z.string(),
      startDate: z.string().nullable(),
      matchMeta: z
        .object({
          confidence: z.number().nullable(),
          rationale: z.array(z.object({ label: z.string(), text: z.string() })),
        })
        .nullable(),
    })
    .nullable(),
  // Server-derived review posture for the visit. Defaulted so an older API's
  // payload renders the pre-follow-up layout rather than an empty surface.
  reviewMode: z.enum(['initial', 'followUp']).default('initial'),
  phase: z.object({
    current: z.number().int().min(1),
    total: z.number().int(),
    names: z.array(z.string()),
  }),
  schedule: z.object({
    segments: z.array(scheduleSegmentSchema),
    totalVisits: z.number().int().nullable(),
    reassess: reassessSchema,
    ladder: z.array(ladderStepSchema),
    /** A later visit proposing a different cadence (ENG-5748). Defaulted so a
     *  payload from an older API parses during a rolling deploy — and because it
     *  is what gates the row's verdict controls, a newer client talking to an
     *  older API renders no controls and so never sends `itemType: 'schedule'`. */
    pendingChanges: z.array(pendingChangeSchema).default([]),
    /** The transcript passage the current cadence was extracted from, when it
     *  came from the transcript rather than the template. */
    sourceEvidence: carePlanEvidenceSchema.nullable().default(null),
  }),
  goals: z.array(goalVmSchema),
  measures: z.array(measureVmSchema),
  actionItems: z.array(actionItemVmSchema),
  // Conditions the visit's Conditions section shows: the plan's addressed
  // conditions plus this visit's extracted drafts.
  // Defaulted so pre-slice-4 payloads (rolling deploy) stay parseable.
  treating: z.array(treatingConditionSchema).default([]),
  checklist: z.object({
    items: z.array(checklistItemSchema),
    completed: z.boolean(),
  }),
  findingsReviewProgress: z.object({
    total: z.number().int(),
    reviewed: z.number().int(),
    approved: z.number().int(),
    rejected: z.number().int(),
    needsReview: z.number().int(),
  }),
  // Server-authoritative activation gate. Nullable/defaulted keeps payloads
  // from an older API parseable during a rolling deployment.
  activationReviewProgress: z
    .object({
      total: z.number().int(),
      reviewed: z.number().int(),
      needsReview: z.number().int(),
    })
    .nullable()
    .default(null),
});

const appt = { appointmentId: z.string() };

export const decideBodySchema = z.object({
  ...appt,
  /** `schedule` targets the CarePlan itself and only ever carries a
   *  `proposalId` — there is no schedule "item" to attest on its own. */
  itemType: z.enum(['goal', 'measure', 'action-item', 'schedule']),
  id: z.string(),
  action: z.enum(['confirm', 'reject', 'undo', 'delete']),
  /** Required for confirm/reject/undo of a CHANGE (branch B); absent = the
   *  verdict targets the item itself (branch A). */
  proposalId: z.string().optional(),
  /** Additive target identity for measure rows. `observationId: null` means
   *  this is a plan target without a capture for the current visit. */
  observationId: z.string().nullable().optional(),
  measureCode: measureCodeSchema.optional(),
});

export const decideBulkBodySchema = z.object({
  ...appt,
  section: z.enum(['goals', 'measures', 'action-items']),
  action: z.enum(['confirm', 'reject']),
});

export const editItemBodySchema = z.discriminatedUnion('itemType', [
  z.object({
    ...appt,
    itemType: z.literal('goal'),
    id: z.string(),
    text: z.string().min(1),
    targetDate: z.string().nullable(),
    done: z.boolean(),
    isPrimary: z.boolean(),
    measureTargets: z.array(measureTargetSchema).optional(),
    /** true on branches A/B (Save = implicit confirm); false on branch C
     *  (plain save, no review semantics). */
    implicitConfirm: z.boolean(),
  }),
  z.object({
    ...appt,
    itemType: z.literal('measure'),
    id: z.string(),
    /** Additive target-only support. Old clients omit these and keep using id
     *  as the Observation id. */
    observationId: z.string().nullable().optional(),
    /**
     * Record this visit's reading for an established measure that has none yet —
     * a row carried in from an earlier visit, or a target-only row. The named
     * Observation is the identity template (code, body site, category), never
     * the write target: a new Observation is created against this visit's
     * encounter, so the earlier visit's reading is left untouched (ENG-5761).
     * Sent with `observationId: null`. Optional and additive; old clients omit
     * it and keep editing in place.
     */
    basedOnObservationId: z.string().optional(),
    measureCode: measureCodeSchema.optional(),
    value: z.number().nullable(),
    stringValue: z.string().nullable(),
    unit: z.string().nullable(),
    /** Measure edit-form fields (final design). Optional/additive — a client
     *  that only logs the this-visit value omits them. name → Observation
     *  display; targetValue/targetUnit + direction → the goal target's
     *  detailQuantity (direction 'higher' → '>=', 'lower' → '<='). */
    name: z.string().min(1).optional(),
    targetValue: z.number().nullable().optional(),
    targetUnit: z.string().nullable().optional(),
    direction: z.enum(['higher', 'lower']).optional(),
    /** Which goal's target entry the threshold edit addresses, so a measure
     *  several goals track carries a threshold per goal. Absent, the first goal
     *  tracking the measure owns it (the pre-ENG-5805 behavior). */
    goalId: z.string().optional(),
    implicitConfirm: z.boolean(),
  }),
  z.object({
    ...appt,
    itemType: z.literal('action-item'),
    id: z.string(),
    text: z.string().min(1),
    implicitConfirm: z.boolean(),
  }),
]);

export const setPhaseBodySchema = z.object({
  ...appt,
  phase: z.number().int().min(1),
});

export const setScheduleBodySchema = z.object({
  ...appt,
  segments: z.array(scheduleSegmentSchema),
  reassess: reassessSchema.omit({ bookedManually: true }).extend({
    afterVisits: z.number().int().min(2).nullable(),
    afterDays: z.number().int().min(1).nullable(),
  }),
  reassessBooked: z.boolean(),
});

export const addGoalBodySchema = z.object({
  ...appt,
  text: z.string().min(1),
  targetDate: z.string().nullable(),
  isPrimary: z.boolean().optional(),
  /** Optional so old clients that never send it keep the not-done default. */
  done: z.boolean().optional(),
  measureTargets: z.array(measureTargetSchema).optional(),
});

/** Clinician-added objective measure: an attested Observation, optionally
 *  pointed at the goal that will track it. */
export const addMeasureBodySchema = z.object({
  ...appt,
  name: z.string().min(1),
  /** The catalog entry the clinician picked for `name`, which codes the created
   *  Observation with the catalog's own system/code. Absent (a free-text name, or
   *  a client predating the picker) the coding is minted from the name. */
  measureKey: z.string().optional(),
  /** The goal to point at the new measure, same gesture as `setGoalTarget`.
   *  Omitted, the measure is recorded and tracked by no goal. */
  goalId: z.string().optional(),
  /** The threshold to author for the goal named by `goalId`, at creation time.
   *  Update-only: it rewrites the target entry that link just made, and mints
   *  nothing when no goal tracks the measure. */
  targetValue: z.number().nullable().optional(),
  targetUnit: z.string().nullable().optional(),
  direction: z.enum(['higher', 'lower']).optional(),
  /** This visit's reading for the new measure, and the body site it was taken
   *  at. Optional and additive: a client that only names a measure omits them
   *  and the Observation is created without a value, as before. `unit` is the
   *  reading's own; the target's rides `targetUnit`. */
  value: z.number().nullable().optional(),
  unit: z.string().nullable().optional(),
  bodySite: z.string().nullable().optional(),
});

/** Clinician-added action item: a pending ServiceRequest leaf (text-only). */
export const addActionItemBodySchema = z.object({
  ...appt,
  text: z.string().min(1),
});

export const activateBodySchema = z.object({ ...appt });

export const findingResourceTypeSchema = z.enum([
  'Condition',
  'Observation',
  'Goal',
]);

const findingTargetSchema = z.object({
  resourceType: findingResourceTypeSchema,
  id: z.string().min(1),
});

export const decideFindingsBodySchema = z.object({
  ...appt,
  decisions: z
    .array(
      findingTargetSchema.extend({
        decision: z.enum(['approve', 'reject']),
      }),
    )
    .min(1),
});

export const undoFindingsBodySchema = z.object({
  ...appt,
  targets: z.array(findingTargetSchema).min(1),
});

export const editFindingBodySchema = findingTargetSchema.extend({
  ...appt,
  display: z.string().min(1),
  targetDate: z.string().nullable().optional(),
  done: z.boolean().optional(),
  isPrimary: z.boolean().optional(),
});

export const dismissFindingBodySchema = findingTargetSchema.extend({
  ...appt,
});

/** Observation is absent by design: a measure never joins the plan directly,
 *  it rides the goal that targets it. */
export const linkableResourceTypeSchema = z.enum([
  'Condition',
  'Goal',
  'ServiceRequest',
]);

/** A goal tracks a set of measures. `action: 'add'` starts tracking the picked
 *  observation's measure alongside the ones the goal already has; `remove`
 *  stops tracking the one `measureCode` + `bodySite` names. Omitting `action`
 *  is single-target v1, which pre-set clients speak: the pick REPLACES the
 *  goal's coded-measure entries and `null` clears them. */
export const setGoalTargetBodySchema = z
  .object({
    ...appt,
    goalId: z.string().min(1),
    /** Optional only so a `remove` can omit it: an old api-nest rejects such a
     *  payload outright rather than reading it as v1's clear-all. An `add`
     *  degrades on that server to a v1 replace (unknown keys stripped), which
     *  is accepted while the feature is dark. */
    observationId: z.string().min(1).nullable().optional(),
    action: z.enum(['add', 'remove']).optional(),
    measureCode: measureCodeSchema.optional(),
    /** Which of a measure's sided entries to stop tracking. `null` names the
     *  entry that records no site, and only that one. */
    bodySite: z.string().nullable().optional(),
  })
  .superRefine((body, ctx) => {
    if (body.action === 'remove') {
      if (!body.measureCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['measureCode'],
          message: 'measureCode is required to remove a tracked measure',
        });
      }
      if (body.bodySite === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bodySite'],
          message: 'bodySite is required to remove a tracked measure',
        });
      }
      return;
    }
    if (body.observationId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['observationId'],
        message: 'observationId is required',
      });
      return;
    }
    if (body.action === 'add' && body.observationId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['observationId'],
        message: 'observationId is required to add a tracked measure',
      });
    }
  });

export const setPlanLinkBodySchema = z.object({
  ...appt,
  resourceType: linkableResourceTypeSchema,
  id: z.string().min(1),
  linked: z.boolean(),
});

export const measureSeriesResponseSchema = z.object({
  measures: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      /**
       * Which side/site the series belongs to. A bilateral measure produces one
       * series PER SITE — "shoulder internal rotation" on the left and on the
       * right are different quantities and must not share a trend, or one side's
       * reading becomes the other's previous value and the delta inverts.
       *
       * Additive and nullable so an older client that ignores it, or a series
       * whose observations record no site, still reads the same as before.
       */
      bodySite: z.string().nullable().default(null),
      points: z.array(trendPointSchema).max(5),
    }),
  ),
});

export const recapResponseSchema = z.object({
  visitNumber: z.number().int().nullable(),
  phase: z
    .object({ current: z.number().int(), name: z.string().nullable() })
    .nullable(),
  goals: z.array(
    z.object({
      id: z.string(),
      text: z.string(),
      done: z.boolean(),
      targetDate: z.string().nullable(),
    }),
  ),
  lastVisitActionItems: z.array(z.object({ id: z.string(), text: z.string() })),
  measuresDue: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      lastValue: z.number().nullable(),
      unit: z.string().nullable(),
      lastDate: z.string().nullable(),
    }),
  ),
});

export const generateForVisitBodySchema = z.object({
  appointmentId: z.string(),
  /**
   * Replace the visit's existing draft. The clinician confirms this after a
   * `scheduled: false, reason: 'draft-exists'` result; without it the endpoint
   * never destroys a draft.
   */
  force: z.boolean().optional().default(false),
});

/**
 * Refusals that the UI must branch on (raise the overwrite confirm, explain the
 * plan is already activated) ride the 200 body rather than a thrown 4xx: the
 * global exception filter flattens every HttpException to
 * `{ statusCode, path, message }`, so a thrown status can't carry a
 * machine-readable reason. Genuine errors — no access, unknown appointment,
 * visit without a transcript — still throw and surface as 403/404/400.
 */
export const generateForVisitResultSchema = z.discriminatedUnion('scheduled', [
  z.object({
    scheduled: z.literal(true),
    /** Which pipeline stage was enqueued — seeds the frontend poll state. */
    stage: z.enum(['findings', 'care-plan']),
    replacedDraft: z.boolean(),
  }),
  z.object({
    scheduled: z.literal(false),
    reason: z.enum(['draft-exists', 'plan-activated']),
  }),
]);

const tenantParams = z.object({ tenantId: z.string() });
const okResult = z.object({ ok: z.literal(true) });
const errorResponse = z.object({ error: z.string() });

export const carePlansContract = c.router(
  {
    getCarePlan: {
      method: 'GET',
      path: '/',
      pathParams: tenantParams,
      query: z.object({ appointmentId: z.string() }),
      responses: { 200: carePlanViewModelSchema, 403: errorResponse },
      summary:
        "Assemble the appointment's render-ready care-plan view model (plan null when none exists — the findings-only floor).",
    },
    generateForVisit: {
      method: 'POST',
      path: '/generate',
      pathParams: tenantParams,
      body: generateForVisitBodySchema,
      responses: {
        200: generateForVisitResultSchema,
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        "Generate a care plan off one clinician-selected visit (ENG-5528). Runs the same per-visit pipeline as the end-of-encounter path: re-extracts clinical findings first when the visit has none, otherwise enqueues generation directly. `force` replaces the visit's existing draft (and resets its Tailor thread); an already-activated plan is never replaced.",
    },
    decide: {
      method: 'POST',
      path: '/items/decide',
      pathParams: tenantParams,
      body: decideBodySchema,
      responses: {
        200: okResult,
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        'Apply one branch-aware verdict (confirm/reject/undo/delete) to a plan item or its pending change.',
    },
    decideBulk: {
      method: 'POST',
      path: '/items/decide-bulk',
      pathParams: tenantParams,
      body: decideBulkBodySchema,
      responses: {
        200: z.object({ approved: z.number(), rejected: z.number() }),
        400: errorResponse,
        403: errorResponse,
        422: errorResponse,
      },
      summary:
        "Per-section 'Confirm all': applies the verdict to every reviewable (added/changed) item in the section.",
    },
    edit: {
      method: 'POST',
      path: '/items/edit',
      pathParams: tenantParams,
      body: editItemBodySchema,
      responses: {
        200: okResult,
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        'Inline-edit Save: applies the clinician values; implicitConfirm=true also counts as the review verdict (branches A/B).',
    },
    decideFindings: {
      method: 'POST',
      path: '/findings/decide',
      pathParams: tenantParams,
      body: decideFindingsBodySchema,
      responses: {
        200: z.object({ approved: z.number(), rejected: z.number() }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        'Confirm or reject appointment findings through the Care Plan review surface.',
    },
    undoFindings: {
      method: 'POST',
      path: '/findings/undo',
      pathParams: tenantParams,
      body: undoFindingsBodySchema,
      responses: {
        200: z.object({ reverted: z.number() }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        'Return confirmed or rejected appointment findings to pending review.',
    },
    editFinding: {
      method: 'POST',
      path: '/findings/edit',
      pathParams: tenantParams,
      body: editFindingBodySchema,
      responses: {
        200: z.object({ edited: z.literal(true) }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        'Edit and implicitly confirm one appointment finding from the Care Plan review surface.',
    },
    dismissFinding: {
      method: 'POST',
      path: '/findings/dismiss',
      pathParams: tenantParams,
      body: dismissFindingBodySchema,
      responses: {
        200: z.object({ dismissed: z.literal(true) }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        'Soft-delete one appointment finding from the Care Plan review surface.',
    },
    setPlanLink: {
      method: 'POST',
      path: '/items/set-plan-link',
      pathParams: tenantParams,
      body: setPlanLinkBodySchema,
      responses: {
        200: okResult,
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        "Join a confirmed item to the visit's care plan, or return it to the chart record only.",
    },
    setGoalTarget: {
      method: 'POST',
      path: '/items/set-goal-target',
      pathParams: tenantParams,
      body: setGoalTargetBodySchema,
      responses: {
        200: okResult,
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        "Point a goal at the objective measure it tracks, or clear the goal's target.",
    },
    setPhase: {
      method: 'POST',
      path: '/phase',
      pathParams: tenantParams,
      body: setPhaseBodySchema,
      responses: {
        200: okResult,
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
      },
      summary:
        'Manually set the current phase and update activity and stick statuses in one transaction.',
    },
    setSchedule: {
      method: 'POST',
      path: '/schedule',
      pathParams: tenantParams,
      body: setScheduleBodySchema,
      responses: {
        200: okResult,
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
      },
      summary:
        'Write the cadence segments + reassessment marker (incl. the manual booked checkbox) onto the plan.',
    },
    addGoal: {
      method: 'POST',
      path: '/goals',
      pathParams: tenantParams,
      body: addGoalBodySchema,
      responses: {
        200: z.object({ id: z.string() }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
      },
      summary:
        'Clinician-authored goal: created live (no review pass) and attached to the plan; isPrimary clears the previous primary.',
    },
    addMeasure: {
      method: 'POST',
      path: '/measures',
      pathParams: tenantParams,
      body: addMeasureBodySchema,
      responses: {
        200: z.object({ id: z.string() }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
      },
      summary:
        'Clinician-authored objective measure: an attested Observation, pointed at the goal named by `goalId` when one is sent.',
    },
    addActionItem: {
      method: 'POST',
      path: '/action-items',
      pathParams: tenantParams,
      body: addActionItemBodySchema,
      responses: {
        200: z.object({ id: z.string() }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
      },
      summary:
        'Clinician-authored action item: a pending ServiceRequest leaf attached to the plan (text-only).',
    },
    activate: {
      method: 'POST',
      path: '/activate',
      pathParams: tenantParams,
      body: activateBodySchema,
      responses: {
        200: z.object({ carePlanId: z.string() }),
        400: errorResponse,
        403: errorResponse,
        404: errorResponse,
        422: errorResponse,
      },
      summary:
        'Activate the draft plan: decode → recompile accepted from the approval date → replace in place; edits + matched items survive.',
    },
    getMeasureSeries: {
      method: 'GET',
      path: '/measures',
      pathParams: tenantParams,
      query: z.object({ appointmentId: z.string() }),
      responses: { 200: measureSeriesResponseSchema, 403: errorResponse },
      summary:
        'Verified measure series (≤5 most recent points per tracked measure key, chronological) for the trend tooltips.',
    },
    getMeasureCatalog: {
      method: 'GET',
      path: '/measure-catalog',
      pathParams: tenantParams,
      responses: {
        200: z.object({ measures: z.array(measureCatalogEntrySchema) }),
        403: errorResponse,
      },
      summary:
        "Return this tenant's objective-measure catalog. V1 serves the shared default catalog through a tenant-aware boundary.",
    },
    getRecap: {
      method: 'GET',
      path: '/recap',
      pathParams: tenantParams,
      query: z.object({ appointmentId: z.string() }),
      responses: { 200: recapResponseSchema, 403: errorResponse },
      summary:
        "Deterministic pre-visit recap (goals · last visit's action items · measures due) — instant FHIR reads, no LLM.",
    },
  },
  { pathPrefix: '/:tenantId/care-plans' },
);

export type CarePlanViewModel = z.infer<typeof carePlanViewModelSchema>;
export type CarePlanReviewMode = CarePlanViewModel['reviewMode'];
export type CarePlanEvidence = z.infer<typeof carePlanEvidenceSchema>;
export type ItemState = z.infer<typeof itemStateSchema>;
export type ItemDecision = z.infer<typeof decisionSchema>;
export type PendingChange = z.infer<typeof pendingChangeSchema>;
export type MeasureTarget = z.infer<typeof measureTargetSchema>;
export type GoalVm = z.infer<typeof goalVmSchema>;
export type MeasureVm = z.infer<typeof measureVmSchema>;
export type ActionItemVm = z.infer<typeof actionItemVmSchema>;
export type TreatingCondition = z.infer<typeof treatingConditionSchema>;
export type ScheduleSegmentVm = z.infer<typeof scheduleSegmentSchema>;
export type LadderStepVm = z.infer<typeof ladderStepSchema>;
export type ChecklistItemVm = z.infer<typeof checklistItemSchema>;
export type TrendPointVm = z.infer<typeof trendPointSchema>;
export type CarriedMeasure = z.infer<typeof carriedMeasureSchema>;
export type DecideBody = z.infer<typeof decideBodySchema>;
export type DecideBulkBody = z.infer<typeof decideBulkBodySchema>;
export type EditItemBody = z.infer<typeof editItemBodySchema>;
export type SetScheduleBody = z.infer<typeof setScheduleBodySchema>;
export type AddGoalBody = z.infer<typeof addGoalBodySchema>;
export type AddMeasureBody = z.infer<typeof addMeasureBodySchema>;
export type AddActionItemBody = z.infer<typeof addActionItemBodySchema>;
export type FindingResourceType = z.infer<typeof findingResourceTypeSchema>;
export type MeasureCatalogEntry = z.infer<typeof measureCatalogEntrySchema>;
export type DecideFindingsBody = z.infer<typeof decideFindingsBodySchema>;
export type UndoFindingsBody = z.infer<typeof undoFindingsBodySchema>;
export type EditFindingBody = z.infer<typeof editFindingBodySchema>;
export type DismissFindingBody = z.infer<typeof dismissFindingBodySchema>;
export type LinkableResourceType = z.infer<typeof linkableResourceTypeSchema>;
export type SetPlanLinkBody = z.infer<typeof setPlanLinkBodySchema>;
export type SetGoalTargetBody = z.infer<typeof setGoalTargetBodySchema>;
export type RecapResponse = z.infer<typeof recapResponseSchema>;
export type GenerateForVisitBody = z.infer<typeof generateForVisitBodySchema>;
export type GenerateForVisitResult = z.infer<
  typeof generateForVisitResultSchema
>;
